import { type AnyAction } from 'redux';
import { requestProjectLedger } from '../ledgerRequest';
import { call, CallEffect, ForkEffect, put, PutEffect, takeLatest } from 'redux-saga/effects';

import {
	activateDocument,
	addProject,
	approveRequest,
	rejectRequest,
	completeProjectActivity,
	getApprovals,
	deleteActivityDocuments,
	fetchActivityDocuments,
	getProjectActivities,
	getProjectDetails,
	getProjects,
	scheduleSiteVisit,
	updateActivityDetails,
	updateProjectDetails,
	deleteProject,
	uploadActivityDocuments,
	getLedgerData,
	recalculateLedger,
	getOrders,
	dropdownUser,
	addProjectDiscount,
	fetchProjectAssignees,
	applyPromoCode,
	removePromoCode,
	getProjectDiscounts,
	getWarrantyClaims,
	getTechnicianWarrantyClaims,
	getWarrantyClaimById,
	updateWarrantyClaim,
	approveWarrantyClaim,
	rejectWarrantyClaim,
	scheduleWarrantyClaim,
	updateWarrantyClaimScheduleDetails,
	submitWarrantyQuote,
	submitWarrantyFinalQuote,
	updateWarrantyCharges,
	updateWarrantyClaimStatuses,
	updateWarrantyClaimDiscount,
	getDescopeApprovals,
	approveDescopeRequest,
	fetchWarrantyDocuments,
	getProjectWarranty,
	putLifetimeWarranty,
	editDiscountAmount,
	editCashDiscountAmount,
	getOptimizedCutlistData
} from './api';
import {
	handleEmptyNullValues,
	handleSortingValues,
	transformUploadData,
	uploadDocumentArr,
	uploadFile
} from '@utilities';
import { SORT_COLUMN_KEYS } from '@containers/leads/allLeads/constants';
import { endOfDay } from 'date-fns';
import { ProjectsActionType } from './enums';
import { autocompleteUser } from '../user/api';

type ProjectsSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type ProjectsSagaForEffect = Generator<ForkEffect<void>>;

const projectsFiltersObject = (obj: Record<string, any>) => {
	const transformData = {
		filters: {
			...obj.filters,
			status: !!obj.filters.status?.length ? obj.filters.status.map(data => data.value) : [],
			// subStatus: !!obj.filters.subStatus?.length
			// 	? obj.filters.subStatus.map(status => {
			// 			return status.key;
			// 	  })
			// 	: [],
			leadId: (() => {
				const ln = obj.filters?.leadName;
				if (!ln) return [];
				return Array.isArray(ln) ? ln[0]?.leadId : ln?.leadId;
			})(),
			collaborateId: !!obj.filters?.collaborate?.length
				? obj?.filters?.collaborate?.map(collaborator => collaborator?.userId)
				: [],
			assigneeId: !!obj.filters?.assignee?.length ? obj?.filters?.assignee[0]?.userId : [],
			updatedOnDateStart: !!obj.filters.updatedOnDate?.length
				? endOfDay(obj.filters.updatedOnDate[0]).getTime()
				: null,
			updatedOnDateEnd: !!obj.filters.updatedOnDate?.length
				? endOfDay(obj.filters.updatedOnDate[1]).getTime()
				: null
		}
	};
	delete transformData?.filters?.collaborate;
	delete transformData?.filters?.assignee;
	delete transformData?.filters?.leadName;

	return {
		...obj,
		filters: transformData.filters
	};
};

export function* fetchProjects(action: AnyAction): ProjectsSagaEffect {
	const { projectsFilters, errorCB } = action.payload;
	let transformedFilters = handleSortingValues(projectsFilters, SORT_COLUMN_KEYS);
	const transformedData = projectsFiltersObject(transformedFilters);
	transformedFilters = handleEmptyNullValues(transformedData);

	try {
		const { data }: any = yield call(getProjects, handleEmptyNullValues(transformedFilters));
		const projectsList = data.projects;
		yield put({
			type: ProjectsActionType.STORE_PROJECTS,
			payload: { projectsList, totalRecords: data.totalRecords }
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_FETCH_PROJECTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchOrders(action: AnyAction): ProjectsSagaEffect {
	const { projectsFilters, errorCB } = action.payload;
	let transformedFilters = handleSortingValues(projectsFilters, SORT_COLUMN_KEYS);
	const transformedData = projectsFiltersObject(transformedFilters);
	transformedFilters = handleEmptyNullValues(transformedData);

	try {
		const { data }: any = yield call(getOrders, handleEmptyNullValues(transformedFilters));
		const projectsList = data.projects;
		yield put({
			type: ProjectsActionType.STORE_ORDERS,
			payload: { projectsList, totalRecords: data.totalRecords }
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_FETCH_ORDERS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* addProjectEffect(action: AnyAction): ProjectsSagaEffect {
	const { formData, successCB, errorCB } = action.payload;

	const transformedData = {
		...formData,
		leadId: typeof formData?.leadId === 'number' ? formData?.leadId : formData?.leadId?.leadId
		// interiorHandoverDate: formData?.interiorHandoverDate?.getTime(),
		// assigneeId: formData?.assigneeId?.userId,
		// collaborateId: formData?.collaborateId && formData?.collaborateId?.map(collaborate => collaborate.userId)
	};

	const projectDetails = handleEmptyNullValues(transformedData);
	try {
		const { data }: any = yield call(addProject, projectDetails);
		successCB(data);
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchProjectDetailsEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, projectId, errorCB, successCB } = action.payload;
	try {
		const { data }: any = yield call(getProjectDetails, ( projectId || projectID));
		yield put({ type: ProjectsActionType.STORE_PROJECT_DETAILS, payload: { data } });
		if (successCB) successCB(data);
	} catch (error: unknown) {
		if (errorCB) errorCB(error?.message);
		yield put({
			type: ProjectsActionType.ERROR_FETCH_PROJECTS_DETAILS,
			payload: {
				error
			}
		});
	}
}

export function* updateProjectDetailsEffect(action: AnyAction): ProjectsSagaEffect {
	const { formData, projectID, successCB, errorCB } = action.payload;
	const transformedData = {
		...formData,
		// interiorHandoverDate: formData?.interiorHandoverDate?.getTime(),
		assigneeId: formData?.assignee?.userId,
		// assigneeIds: formData?.assignees && formData?.assignees?.map(assignee => assignee.userId),
		newAssigneeIds: formData?.assignees && formData?.assignees,
		collaboratorId: formData?.collaborate && formData?.collaborate?.map(collaborate => collaborate.userId)
	};
	delete transformedData?.collaborate;
	delete transformedData?.assignee;
	delete transformedData?.assignees;

	try {
		yield call(updateProjectDetails, transformedData, projectID);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* deleteProjectEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, successCB, errorCB } = action.payload;

	try {
		yield call(deleteProject, projectID);
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	}
}

export function* fetchCollaboratorAutocompleteEffect(action: AnyAction): ProjectsSagaEffect {
	const { prefix, errorCB } = action.payload;

	try {
		const { data }: any = yield call(autocompleteUser, prefix);
		const collaboratorList = data.users?.map((collaborator: Record<string, any>) => {
			return {
				...collaborator,
				displayName: `${collaborator.fullName} (${collaborator.phoneNumber})`
			};
		});
		yield put({ type: ProjectsActionType.STORE_COLLABORATOR_AUTOCOMPLETE, payload: { data: collaboratorList } });
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_FETCH_COLLABORATOR_AUTOCOMPLETE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchAssigneeAutocompleteEffect(action: AnyAction): ProjectsSagaEffect {
	const { prefix, errorCB } = action.payload;

	try {
		const { data }: any = yield call(autocompleteUser, prefix);
		const assigneeList = data.users?.map((assignee: Record<string, any>) => {
			return {
				...assignee,
				displayName: `${assignee.fullName} (${assignee.phoneNumber})`
			};
		});
		yield put({ type: ProjectsActionType.STORE_ASSIGNEE_AUTOCOMPLETE, payload: { data: assigneeList } });
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_FETCH_COLLABORATOR_AUTOCOMPLETE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchLeadDesignerAutoCompleteEffect(action: AnyAction): ProjectsSagaEffect {
	const { prefix, errorCB } = action.payload;

	const searchText = prefix;
	try {
		const { data }: any = yield call(autocompleteUser, searchText);
		yield put({ type: ProjectsActionType.STORE_AUTOCOMPLETE_LEAD_DESIGNER, payload: data.users });
	} catch (error: unknown) {
		yield put({ type: ProjectsActionType.ERROR_FETCH_AUTOCOMPLETE_LEAD_DESIGNER, payload: error });
		errorCB(error?.message);
	}
}

export function* fetchProjectActivitiesEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, errorCB } = action.payload;

	try {
		const { data }: any = yield call(getProjectActivities, projectID);

		yield put({
			type: ProjectsActionType.STORE_PROJECT_ACTIVITIES,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_PROJECT_ACTIVITIES,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* completeProjectActivityEffect(action: AnyAction): ProjectsSagaEffect {
	const { rowData, schedulerId, successCB, errorCB } = action.payload;
	try {
		yield call(completeProjectActivity, handleEmptyNullValues(rowData), schedulerId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchApproval(action: AnyAction): ProjectsSagaEffect {
	const { approvalFilters, errorCB } = action.payload;
	const transformedFilters = handleEmptyNullValues(approvalFilters);
	try {
		const { data }: any = yield call(getApprovals, handleEmptyNullValues(transformedFilters));
		const approvalList = data.data;
		yield put({
			type: ProjectsActionType.STORE_ALL_APPROVAL,
			payload: { approvalList, totalRecords: data.totalRecords }
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_ALL_APPROVAL,
			payload: {
				error
			}
		});
		errorCB();
	}
}

export function* updateActivityDetailsEffect(action: AnyAction): ProjectsSagaEffect {
	const { schedulerId, successCB, errorCB, values } = action.payload;
	try {
		yield call(updateActivityDetails, handleEmptyNullValues(values), schedulerId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* approveRequestEffect(action: AnyAction): ProjectsSagaEffect {
	const { approvalId, successCB, errorCB } = action.payload;
	try {
		yield call(approveRequest, approvalId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* rejectRequestEffect(action: AnyAction): ProjectsSagaEffect {
	const { approvalId, successCB, errorCB } = action.payload;
	try {
		yield call(rejectRequest, approvalId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchDescopeApprovalsEffect(action: AnyAction): ProjectsSagaEffect {
	const { errorCB } = action.payload;
	try {
		const { data }: any = yield call(getDescopeApprovals);
		const descopeApprovals = data.data || [];
		yield put({
			type: ProjectsActionType.STORE_DESCOPE_APPROVALS,
			payload: { descopeApprovals, totalRecords: data.totalRecords || 0 }
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_DESCOPE_APPROVALS,
			payload: {
				error
			}
		});
		errorCB && errorCB(error?.message);
	}
}

export function* approveDescopeRequestEffect(action: AnyAction): ProjectsSagaEffect {
	const { approvalId, successCB, errorCB } = action.payload;
	try {
		yield call(approveDescopeRequest, approvalId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchWarrantyClaimsEffect(action: AnyAction): ProjectsSagaEffect {
	const { warrantyFilters, forTechnician, errorCB } = action.payload;
	const { offset = 0, pageSize = 10, filters } = warrantyFilters || {};
	const requestPayload = handleEmptyNullValues({
		offset,
		pageSize,
		filters
	});
	try {
		const { data }: any = yield call(
			forTechnician ? getTechnicianWarrantyClaims : getWarrantyClaims,
			requestPayload
		);
		const warrantyClaims = data?.warrantyClaims ?? data?.warranty_claims ?? [];
		yield put({
			type: ProjectsActionType.STORE_WARRANTY_CLAIMS,
			payload: {
				warrantyClaims,
				totalRecords: data?.totalRecords ?? data?.total_records ?? 0
			}
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_WARRANTY_CLAIMS,
			payload: {
				error
			}
		});
		errorCB?.(error?.message);
	}
}

export function* approveWarrantyClaimEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(approveWarrantyClaim, claimId);
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* rejectWarrantyClaimEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(rejectWarrantyClaim, claimId);
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* scheduleWarrantyClaimEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data: response }: any = yield call(scheduleWarrantyClaim, claimId, data);
		successCB?.(response);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* updateWarrantyClaimScheduleEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data: response }: any = yield call(updateWarrantyClaimScheduleDetails, claimId, data);
		successCB?.(response);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* submitWarrantyQuoteEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, files, successCB, errorCB, finalCB } = action.payload;
	try {
		// 1) Submit quote + attachment filenames → backend returns signed PUT URLs
		const { data: response }: any = yield call(submitWarrantyQuote, claimId, data);
		const document = response?.document;
		const attachments = document?.attachments || [];
		const documentId = document?.documentId ?? document?.document_id;

		// 2) PUT each before-image binary to its signed URL (same pattern as feedback/products)
		if (files?.length) {
			if (!attachments.length || documentId == null) {
				throw new Error('Quote response missing signed upload URLs for before images');
			}

			for (let i = 0; i < files.length; i++) {
				const attachment = attachments[i];
				const uploadUrl = attachment?.documentUrl ?? attachment?.document_url;
				const fileName =
					attachment?.fileName ?? attachment?.file_name ?? files[i]?.name;
				if (!uploadUrl) {
					throw new Error(`Missing signed URL for before image: ${fileName || i + 1}`);
				}
				yield call(uploadFile, uploadUrl, files[i], fileName);
			}

			// 3) Activate uploaded document so URLs move PENDING → SUCCESSFUL
			yield call(activateDocument, { documentIds: [documentId] });
		}

		successCB?.(response);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* submitWarrantyFinalQuoteEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, files, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data: response }: any = yield call(submitWarrantyFinalQuote, claimId, data);
		const document = response?.document;
		const attachments = document?.attachments || [];
		const documentId = document?.documentId ?? document?.document_id;

		if (files?.length) {
			if (!attachments.length || documentId == null) {
				throw new Error('Final quote response missing signed upload URLs for after images');
			}

			for (let i = 0; i < files.length; i++) {
				const attachment = attachments[i];
				const uploadUrl = attachment?.documentUrl ?? attachment?.document_url;
				const fileName =
					attachment?.fileName ?? attachment?.file_name ?? files[i]?.name;
				if (!uploadUrl) {
					throw new Error(`Missing signed URL for after image: ${fileName || i + 1}`);
				}
				yield call(uploadFile, uploadUrl, files[i], fileName);
			}

			yield call(activateDocument, { documentIds: [documentId] });
		}

		successCB?.(response);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* updateWarrantyChargesEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data: response }: any = yield call(updateWarrantyCharges, claimId, data);
		successCB?.(response);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* updateWarrantyClaimStatusesEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data: response }: any = yield call(updateWarrantyClaimStatuses, claimId, data);
		successCB?.(response);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* updateWarrantyClaimDiscountEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data: response }: any = yield call(updateWarrantyClaimDiscount, claimId, data);
		successCB?.(response);
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* fetchWarrantyClaimByIdEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getWarrantyClaimById, claimId);
		yield put({
			type: ProjectsActionType.STORE_WARRANTY_CLAIM_DETAILS,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_WARRANTY_CLAIM_DETAILS,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* updateWarrantyClaimEffect(action: AnyAction): ProjectsSagaEffect {
	const { claimId, data, successCB, errorCB, finalCB } = action.payload;
	try {
		yield call(updateWarrantyClaim, claimId, data);
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	} finally {
		finalCB?.();
	}
}

export function* fetchWarrantyDocumentsEffect(action: AnyAction): ProjectsSagaEffect {
	const { documentIds, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchWarrantyDocuments, { documentIds: documentIds });
		yield put({
			type: ProjectsActionType.STORE_WARRANTY_DOCUMENTS,
			payload: data
		});
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchProjectWarrantyEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getProjectWarranty, projectId);
		yield put({
			type: ProjectsActionType.STORE_PROJECT_WARRANTY,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_PROJECT_WARRANTY,
			payload: { error }
		});
		errorCB?.(error?.message);
	}
}

export function* putLifetimeWarrantyEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectId, successCB, errorCB } = action.payload;
	try {
		yield call(putLifetimeWarranty, projectId);
		// Refresh warranty data after setting lifetime warranty
		yield put({
			type: ProjectsActionType.FETCH_PROJECT_WARRANTY,
			payload: { projectId, errorCB: () => {} }
		});
		successCB?.();
	} catch (error: unknown) {
		errorCB?.(error?.message);
	}
}

export function* scheduleSiteVisitEffect(action: AnyAction): ProjectsSagaEffect {
	const { schedulerId, successCB, errorCB } = action.payload;
	try {
		yield call(scheduleSiteVisit, schedulerId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* uploadActivityDocumentEffect(action: AnyAction): ProjectsSagaEffect {
	const { formData, schedulerId, errorCB, successCB } = action.payload;
	const currentFileType = 'MEDIA';

	const transformedPayload = transformUploadData(formData.document, currentFileType);
	const newFormData = {
		...formData,
		...transformedPayload
	};
	delete newFormData.transformedData;

	const transformedObject = {
		...newFormData,
		document: {
			referenceId: newFormData.document[0].referenceId,
			// Scheduler activity docs are persisted against the SCHEDULER reference type. The
			// activity/stage (e.g. "Pre-Installation Stage") drives category/subCategory, not the
			// document reference type, so mapping it to a stage enum (PRE_INSTALLATION, ...) makes the
			// backend reject it (not a valid DocumentReferenceType).
			referenceType: 'SCHEDULER',
			category: newFormData.document[0].category,
			subCategory: newFormData.document[0].subCategory,
			attachments: newFormData.document[0].files.map(file => ({
				file: file.fileName
			}))
		}
	};
	try {
		const { data } = yield call(uploadActivityDocuments, transformedObject, schedulerId);
		const { attachments, category, subCategory, documentId } = data;
		const { arrPromises, uploadedDocuments } = uploadDocumentArr({ documents: [formData?.document] }, [
			{ category, subCategory, attachments, documentId }
		]);
		yield call(async () => await Promise.all(arrPromises));

		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchFloorPlanImageEffect(action: AnyAction): ProjectsSagaEffect {
	const { documentId, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchActivityDocuments, { documentIds: [documentId] });
		yield put({
			type: ProjectsActionType.STORE_FLOOR_PLAN_IMAGE,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_FLOOR_PLAN_IMAGE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchActivityDocumentsEffect(action: AnyAction): ProjectsSagaEffect {
	const { documentIds, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchActivityDocuments, { documentIds: documentIds });
		yield put({
			type: ProjectsActionType.STORE_ACTIVITY_DOCUMENTS,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_FETCH_ACTIVITY_DOCUMENTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchApprovalDocumentsEffect(action: AnyAction): ProjectsSagaEffect {
	const { documentIds, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchActivityDocuments, { documentIds: documentIds });

		yield put({
			type: ProjectsActionType.STORE_APPROVAL_DOCUMENTS,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_APPROVAL_DOCUMENTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* deleteActivityDocumentsEffect(action: AnyAction): ProjectsSagaEffect {
	const { docId, successCB, errorCB } = action.payload;
	try {
		yield call(deleteActivityDocuments, { ids: [docId] });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchLedgerDataEffect(action: AnyAction): any {
    yield* requestProjectLedger(action.payload, getLedgerData, {
        store: ProjectsActionType.STORE_LEDGER_DATA,
        error: ProjectsActionType.ERROR_LEDGER_DATA
    });
}

export function* recalculateLedgerEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(recalculateLedger, projectID);
		yield put({
			type: ProjectsActionType.STORE_LEDGER_DATA,
			payload: data
		});
		successCB?.();
	} catch (error: unknown) {
		const errorMessage =
			(error as any)?.response?.data?.message ||
			(error as any)?.message ||
			'Failed to recalculate ledger';
		errorCB?.(errorMessage);
	}
}

export function* fetchProjectDiscountsEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getProjectDiscounts, projectID);
		yield put({
			type: ProjectsActionType.STORE_PROJECT_DISCOUNTS,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_PROJECT_DISCOUNTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchDropdownEffect(
	storeActionType: ProjectsActionType,
	errorActionType: ProjectsActionType,
	action: AnyAction
): ProjectsSagaEffect {
	const { department, errorCB } = action.payload;
	try {
		const { data }: any = yield call(dropdownUser, department);
		yield put({
			type: storeActionType,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: errorActionType,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* addProjectDiscountEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, discount, successCB, errorCB } = action.payload;
	const requestBody = {
		projectId: projectID,
		discountType: discount.type === 'percentage' ? 'PERCENTAGE' : 'AMOUNT',
		amount: discount.value,
		percent: discount.value
	};

	if (discount.type === 'percentage') {
		delete requestBody.amount;
	} else {
		delete requestBody.percent;
	}

	try {
		yield call(addProjectDiscount, requestBody, projectID); // Void method, no response data
		successCB();
	} catch (error: unknown) {
		const errorMessage = error?.response?.data?.message || error?.message || 'Failed to apply discount';
		yield put({
			type: ProjectsActionType.ERROR_DISCOUNT,
			payload: {
				error: {
					code: error?.response?.status || 'UNKNOWN',
					message: errorMessage,
					type: 'server'
				}
			}
		});
		errorCB(errorMessage);
	}
}

export function* editDiscountAmountEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, amount, successCB, errorCB } = action.payload;
	try {
		yield call(editDiscountAmount, projectID, amount);
		successCB();
	} catch (error: unknown) {
		const errorMessage = error?.message || 'Failed to edit discount amount';
		errorCB(errorMessage);
	}
}

export function* editCashDiscountAmountEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, amount, successCB, errorCB } = action.payload;
	try {
		yield call(editCashDiscountAmount, projectID, amount);
		successCB();
	} catch (error: unknown) {
		const errorMessage = error?.message || 'Failed to edit cash discount';
		errorCB(errorMessage);
	}
}

export function* fetchProjectAssigneesEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchProjectAssignees, projectID);
		yield put({
			type: ProjectsActionType.STORE_PROJECT_ASSIGNEES,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_PROJECT_ASSIGNEES,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* applyPromoCodeEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, promoCode, successCB, errorCB } = action.payload;
	try {
		yield call(applyPromoCode, { projectId: Number(projectID), promoCode });
		successCB();
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_APPLY_PROMO_CODE,
			payload: { error }
		});
		errorCB(error?.message || 'Failed to apply promo code');
	}
}

export function* removePromoCodeEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, promoCode, successCB, errorCB } = action.payload;
	try {
		yield call(removePromoCode, { projectId: Number(projectID), promoCode });
		successCB();
	} catch (error: unknown) {
		yield put({
			type: ProjectsActionType.ERROR_REMOVE_PROMO_CODE,
			payload: { error }
		});
		errorCB(error?.message || 'Failed to remove promo code');
	}
}

export function* fetchProjectsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_PROJECTS, fetchProjects);
}

export function* fetchOrdersSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_ORDERS, fetchOrders);
}

export function* addProjectSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.ADD_PROJECT, addProjectEffect);
}

export function* fetchProjectDetailsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_PROJECT_DETAILS, fetchProjectDetailsEffect);
}

export function* updateProjectDetailsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPDATE_PROJECT_DETAILS, updateProjectDetailsEffect);
}

export function* fetchCollaboratorAutocompleteSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_COLLABORATOR_AUTOCOMPLETE, fetchCollaboratorAutocompleteEffect);
}

export function* fetchAssigneeAutocompleteSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_ASSIGNEE_AUTOCOMPLETE, fetchAssigneeAutocompleteEffect);
}

export function* fetchLeadDesignerAutoCompleteSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_AUTOCOMPLETE_LEAD_DESIGNER, fetchLeadDesignerAutoCompleteEffect);
}

export function* fetchProjectActivitiesSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_PROJECT_ACTIVITIES, fetchProjectActivitiesEffect);
}

export function* completeProjectActivitySaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.COMPLETE_PROJECT_ACTIVITY, completeProjectActivityEffect);
}

export function* fetchAllApprovalSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_ALL_APPROVAL, fetchApproval);
}

export function* fetchWarrantyClaimsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_WARRANTY_CLAIMS, fetchWarrantyClaimsEffect);
}

export function* approveWarrantyClaimSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.APPROVE_WARRANTY_CLAIM, approveWarrantyClaimEffect);
}

export function* rejectWarrantyClaimSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.REJECT_WARRANTY_CLAIM, rejectWarrantyClaimEffect);
}

export function* scheduleWarrantyClaimSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.SCHEDULE_WARRANTY_CLAIM, scheduleWarrantyClaimEffect);
}

export function* updateWarrantyClaimScheduleSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPDATE_WARRANTY_CLAIM_SCHEDULE, updateWarrantyClaimScheduleEffect);
}

export function* submitWarrantyQuoteSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.SUBMIT_WARRANTY_QUOTE, submitWarrantyQuoteEffect);
}

export function* submitWarrantyFinalQuoteSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.SUBMIT_WARRANTY_FINAL_QUOTE, submitWarrantyFinalQuoteEffect);
}

export function* updateWarrantyChargesSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPDATE_WARRANTY_CHARGES, updateWarrantyChargesEffect);
}

export function* updateWarrantyClaimStatusesSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPDATE_WARRANTY_CLAIM_STATUSES, updateWarrantyClaimStatusesEffect);
}

export function* updateWarrantyClaimDiscountSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPDATE_WARRANTY_CLAIM_DISCOUNT, updateWarrantyClaimDiscountEffect);
}

export function* fetchWarrantyClaimByIdSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_WARRANTY_CLAIM_BY_ID, fetchWarrantyClaimByIdEffect);
}

export function* updateWarrantyClaimSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPDATE_WARRANTY_CLAIM, updateWarrantyClaimEffect);
}

export function* fetchWarrantyDocumentsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_WARRANTY_DOCUMENTS, fetchWarrantyDocumentsEffect);
}

export function* updateActivityDetailsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPDATE_ACTIVITY_DETAILS, updateActivityDetailsEffect);
}

export function* approveRequestSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.APPROVE_REQUEST, approveRequestEffect);
}

export function* rejectRequestSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.REJECT_REQUEST, rejectRequestEffect);
}

export function* scheduleSiteVisitSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.COMPLETE_SITE_VISIT, scheduleSiteVisitEffect);
}

export function* uploadActivityDocumentSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.UPLOAD_ACTIVITY_DOCUMENT, uploadActivityDocumentEffect);
}

export function* fetchActivityDocumentsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_ACTIVITY_DOCUMENTS, fetchActivityDocumentsEffect);
}

export function* deleteActivityDocumentsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.DELETE_ACTIVITY_DOCUMENTS, deleteActivityDocumentsEffect);
}

export function* fetchApprovalDocumentsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_APPROVAL_DOCUMENTS, fetchApprovalDocumentsEffect);
}

export function* fetchLedgerDataSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_LEDGER_DATA, fetchLedgerDataEffect);
}

export function* recalculateLedgerSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.RECALCULATE_LEDGER, recalculateLedgerEffect);
}

export function* fetchProjectDiscountsSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_PROJECT_DISCOUNTS, fetchProjectDiscountsEffect);
}

export function* fetchProjectFloorPlanImageSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_FLOOR_PLAN_IMAGE, fetchFloorPlanImageEffect);
}

export function* fetchAssigneeDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_ASSIGNEE_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_ASSIGNEE_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_ASSIGNEE_DROPDOWN
	);
}

export function* fetchDesignerDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_DESIGNER_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_DESIGNER_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_DESIGNER_DROPDOWN
	);
}

export function* fetchProductionDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_PRODUCTION_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_PRODUCTION_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_PRODUCTION_DROPDOWN
	);
}

export function* fetchQCDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_QC_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_QC_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_QC_DROPDOWN
	);
}

export function* addProjectDiscountSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.ADD_DISCOUNT, addProjectDiscountEffect);
}

export function* editDiscountAmountSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.EDIT_DISCOUNT_AMOUNT, editDiscountAmountEffect);
}

export function* editCashDiscountAmountSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.EDIT_CASH_DISCOUNT_AMOUNT, editCashDiscountAmountEffect);
}

export function* fetchProjectAssigneesSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_PROJECT_ASSIGNEES, fetchProjectAssigneesEffect);
}

export function* applyPromoCodeSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.APPLY_PROMO_CODE, applyPromoCodeEffect);
}

export function* removePromoCodeSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.REMOVE_PROMO_CODE, removePromoCodeEffect);
}

export function* fetchFinanceDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_FINANCE_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_FINANCE_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_FINANCE_DROPDOWN
	);
}

export function* fetchProcurementDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_PROCUREMENT_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_PROCUREMENT_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_PROCUREMENT_DROPDOWN
	);
}

export function* fetchManufacturingDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_MANUFACTURING_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_MANUFACTURING_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_MANUFACTURING_DROPDOWN
	);
}

export function* fetchProjectManagementDropdownSaga(): ProjectsSagaForEffect {
	yield takeLatest(
		ProjectsActionType.FETCH_PROJECT_MANAGEMENT_DROPDOWN,
		fetchDropdownEffect,
		ProjectsActionType.STORE_PROJECT_MANAGEMENT_DROPDOWN,
		ProjectsActionType.ERROR_FETCH_PROJECT_MANAGEMENT_DROPDOWN
	);
}

export function* fetchProjectWarrantySaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_PROJECT_WARRANTY, fetchProjectWarrantyEffect);
}

export function* putLifetimeWarrantySaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.PUT_LIFETIME_WARRANTY, putLifetimeWarrantyEffect);
	yield takeLatest(ProjectsActionType.FETCH_DESCOPE_APPROVALS, fetchDescopeApprovalsEffect);
	yield takeLatest(ProjectsActionType.APPROVE_DESCOPE_REQUEST, approveDescopeRequestEffect);
}

export function* fetchOptimizedCutlistDataEffect(action: AnyAction): ProjectsSagaEffect {
	const { projectID, projectId, successCB, errorCB } = action.payload;
	const resolvedProjectId = projectID ?? projectId;
	try {
		const { data }: any = yield call(getOptimizedCutlistData, resolvedProjectId);
		yield put({
			type: ProjectsActionType.STORE_OPTIMIZED_CUTLIST_DATA,
			payload: { data }
		});
		yield put({
			type: 'STORE_OPTIMISER_DATA',
			payload: { data }
		});
		if (successCB) successCB(data);
	} catch (error: unknown) {
		const errMsg = (error as any)?.message ?? String(error);
		if (errorCB) errorCB(errMsg);
		yield put({
			type: ProjectsActionType.ERROR_OPTIMIZED_CUTLIST_DATA,
			payload: { error: errMsg }
		});
		yield put({
			type: 'ERROR_OPTIMISER_DATA',
			payload: { error: errMsg }
		});
	}
}

export function* fetchOptimizedCutlistDataSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.FETCH_OPTIMIZED_CUTLIST_DATA, fetchOptimizedCutlistDataEffect);
}

export function* deleteProjectSaga(): ProjectsSagaForEffect {
	yield takeLatest(ProjectsActionType.DELETE_PROJECT, deleteProjectEffect);
}
